import { useForm } from '@mantine/form'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { UIButton } from 'src/components/ui/Button'
import { UIForm } from 'src/components/ui/Form'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { UITextArea } from 'src/components/ui/TextField'
import type { UserDecorationRelation } from 'src/services/apollo/generated/graphql'
import { useUpdateUserDecorationRelation } from 'src/services/orpc/decoration'

type RemarkFormValues = {
  userRemark: string
}

export function EditUserDecorationRemarkButton(props: {
  decorationRelation: UserDecorationRelation
}) {
  const { t } = useTranslation(['user'])
  const decorationId = props.decorationRelation.decorationId
  const relationId = props.decorationRelation.id
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const { mutateAsync, isPending } = useUpdateUserDecorationRelation()

  const form = useForm<RemarkFormValues>({
    initialValues: {
      userRemark: props.decorationRelation.userRemark ?? '',
    },
  })

  const onClose = useCallback(() => {
    setOpenEditDialog(false)
    form.reset()
  }, [form])

  const onSubmit = useCallback(
    async (values: RemarkFormValues) => {
      try {
        await mutateAsync({
          decorationRelationId: relationId,
          userRemark: values.userRemark,
        })
        toast.success('Remark updated successfully')
        setOpenEditDialog(false)
      } catch (error) {
        toast.error('Failed to update remark')
        console.error('Failed to update remark:', error)
      }
    },
    [mutateAsync, decorationId, relationId],
  )

  return (
    <>
      <UIButton
        variant={'outlined'}
        size="small"
        className="text-xs capitalize"
        onPress={() => setOpenEditDialog(true)}
      >
        <Text i18nKey="action:edit" />
      </UIButton>
      <UIDialog open={openEditDialog} onClose={onClose}>
        <UIForm form={form} onSubmit={onSubmit}>
          <UIDialogHeader
            heading={t('user:user-decoration.edit-remark.dialog.title', {
              defaultValue: 'Edit Remark',
            })}
          />
          <UIDialogContent>
            <UITextArea
              variant="rounded-normal"
              className="rounded-md resize-none w-full"
              placeholder={t(
                'user:user-decoration.edit-remark.dialog.placeholder',
                {
                  defaultValue: 'Enter your remark here (max 30 characters)',
                },
              )}
              inputMode="text"
              autoCapitalize="off"
              spellCheck="false"
              autoCorrect="off"
              maxLength={30}
              {...form.getInputProps('userRemark')}
            />
          </UIDialogContent>
          <UIDialogFooter>
            <UIButton onPress={onClose} disabled={isPending}>
              <Text i18nKey="action:cancel" />
            </UIButton>
            <UIButton variant="filled" type="submit" busy={isPending}>
              <Text i18nKey="action:confirm" />
            </UIButton>
          </UIDialogFooter>
        </UIForm>
      </UIDialog>
    </>
  )
}
